import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getLocaleFromUrl } from '../../../../shared/services/translate/locale-utils';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { GeolocationService } from '../../../../shared/services/geolocation/geolocation.service';
import { MCAppGatewayService } from '../../../../shared/services/mcappgateway/mcappgateway.service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { LocationCoordinates } from '../../../../shared/model/LocationCoordinates';
import { MonitoringService } from '../../../../shared/services/monitoring/monitoring-service';
import { HospitalComponent } from '../hospital/hospital.component';
import { environment } from '../../../../../environments/environment';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

@Component({
  selector: 'app-pre-login-mobile',
  imports: [CommonModule, TranslateModule, MatProgressSpinnerModule, MatButtonModule, HospitalComponent],
  templateUrl: './pre-login.component.html',
  styleUrl: './pre-login.component.css'
})
export class PreLoginComponent {
  geolocation = inject(GeolocationService);
  mcAppGateway = inject(MCAppGatewayService);
  auth : AuthService = inject(AuthService);

  locale: string = '';
  currentLocation?: LocationCoordinates = undefined;
  public debugText = '';

  getCareNowTitle: string = '';
  getCareNowDescription: string = '';
  loginDescription: string = '';
  findAProvider: string = '';
  findALocation: string = '';
  onlinechatTitle: string = '';
  onlinechatDescription: string = '';

  hospitalOptions: Array<any> = [];
  preloginPhoto: string = '';
  backgroundImageStyle: SafeStyle = '';  
  
  constructor(private translate: TranslateService, private monitoringService: MonitoringService, private sanitizer: DomSanitizer) {
    this.locale = getLocaleFromUrl();
    sessionStorage.setItem("locale", this.locale);
    this.translate.use(this.locale);
 }

  async ngOnInit() {
    this.translate.get('GETCARENOW').subscribe((translated: string) => {
      this.getCareNowTitle = translated;
    });

    this.translate.get('GETCARENOW_DESCRIPTION').subscribe((translated: string) => {
      this.getCareNowDescription = translated;
    });

    this.translate.get('LOGIN_DESCRIPTION').subscribe((translated: string) => {
      this.loginDescription = translated;
    });

    this.translate.get('FINDAPROVIDER').subscribe((translated: string) => {
      this.findAProvider = translated;
    });

    this.translate.get('FINDALOCATION').subscribe((translated: string) => {
      this.findALocation = translated;
    });

    this.translate.get('ONLINECHAT_TITLE').subscribe((translated: string) => {
      this.onlinechatTitle = translated;
    });
    this.translate.get('ONLINECHAT_DESCRIPTION').subscribe((translated: string) => {
      this.onlinechatDescription = translated;
    });

    const locationPermissionAllowed = localStorage.getItem('locationPermissionAllowed');
    const storedLocation = localStorage.getItem('currentLocation');

    // For MyChart Plus App, we expect location to be passed in via pre-login page and stored in local storage,  
    // so we can attempt to use it immediately without prompting for geolocation on init. 

    // For standard mobile web, we will attempt geolocation on init 
    // but fallback to allowing user to trigger it if it fails or times out.
    if (locationPermissionAllowed === 'false') {
      // User has previously denied location permission, 
      // so we can skip trying to get location and just fetch data without it
      this.getData();
    }
    else if (locationPermissionAllowed === 'true' && storedLocation) {
      try {
        this.currentLocation = JSON.parse(storedLocation) as LocationCoordinates;
      } catch (err) {
        console.error("Failed to parse stored location:", err);
        localStorage.removeItem('currentLocation'); // Clear invalid data
      }

      this.getData(); // Fetch care options immediately, using stored location if available
    }
    else {
      // we can attempt to get location on init
      // Add a timeout to the geolocation request
      const geolocationPromise = this.geolocation.getCurrentLocationAsync();
      const timeoutPromise = new Promise<LocationCoordinates>((_, reject) =>
        setTimeout(() => reject(new Error('Geolocation timeout')), 2000) // 2 seconds timeout
      );

      await Promise.race([geolocationPromise, timeoutPromise])
        .then(location => {
            this.currentLocation = location;
            this.debugText = `Lat: ${location.latitude}, Long: ${location.longitude}`;
        })
        .catch(err => {
          console.error("Geolocation failed:", err);
          this.debugText = 'Geolocation not available' + (err ? `: ${err}` : '');
        })
        .finally(() => {
          this.getData();
        });
    }

    this.monitoringService.logEvent('Prelogin', { feature: 'pre-login', platform: 'mobile-plus' });
  }

  async allowLocation() {
    this.geolocation.getCurrentLocation().subscribe(location => {
      this.currentLocation = location;
      this.getData();
    });
  }

  getData() {
    this.getPreloginImage();
    this.getHospitalOptions();
  }

  getPreloginImage() {
    this.mcAppGateway.PostGetPreloginImage(this.currentLocation).subscribe({
      next: (response) => {
        console.log("Prelogin Image fetched successfully:");
        console.log(response);
        this.preloginPhoto = response.photo || "";
        this.refreshBackgroundImageStyle();
      },
      error: (err) => {
        console.error("Prelogin Image fetch failed:", err);
         this.refreshBackgroundImageStyle();
      }
    });
  }

  getHospitalOptions() {
    this.mcAppGateway.PostGetHospital(this.currentLocation).subscribe({
      next: (response) => {
        console.log("Our Hopsital options fetched successfully:", response);
        this.hospitalOptions = response || [];
      }
    });
  }

  onGetCareNow() {  
    var deepLink =  environment.web_get_care_now_deep_link_base_url;
    this.monitoringService.logEvent('Get Care Now', { feature: 'pre-login', platform: 'mobile-plus'}); 
   
    console.log("Get Care Now clicked");
    console.log(deepLink);
      
    window.location.href = deepLink;
  }

  onLogin() {
    var deepLink = "mymemorialcare://";
    this.monitoringService.logEvent('Login', { feature: 'pre-login', platform: 'mobile-plus'}); 

    console.log("Login clicked");

    window.location.href = deepLink;
  }

  openChatBot() {  
    var deepLink = environment.mobile_open_chat_bot_deep_link_base_url;
    this.monitoringService.logEvent('Open Chat Bot', { feature: 'pre-login', platform: 'mobile-plus'});
   
    console.log("Open Chat Bot clicked");
    console.log(deepLink);
      
    window.open(deepLink, '_blank');
  }

  onProviderSearch() {  
    var deepLink = environment.web_provider_search_deep_link_base_url;
    this.monitoringService.logEvent('Provider Search', { feature: 'pre-login', platform: 'mobile-plus' }); 
   
    console.log("Provider Search clicked");
    console.log(deepLink);
     
    window.location.href = deepLink;
  }

  onLocationSearch() {  
    var deepLink = environment.web_location_search_deep_link_base_url;
    this.monitoringService.logEvent('Location Search', { feature: 'pre-login', platform: 'mobile-plus'});

    console.log("Location Search clicked");
    console.log(deepLink);
    
    window.location.href = deepLink;
  }

  refreshBackgroundImageStyle() {
    this.backgroundImageStyle = this.sanitizer.bypassSecurityTrustStyle(`linear-gradient(to right, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 40%, rgba(0, 0, 0, 0) 60%), url('${this.preloginPhoto}')`);;
    console.log(this.backgroundImageStyle);
  }
}